import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Drawer } from "@/components/ui/Drawer";
import { Spinner } from "@/components/ui/v2/spinner";
import { useCompanyGetTeamMembersQuery } from "@/services/(company)/api.teams";
import { startCase } from "lodash";
import {
  CalendarPlus,
  Component,
  Crown,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PATHS } from "@/routes/paths";
import { TEAM_STEPS } from "../create";
import { hTabKey } from "@/components/HorizontalTabs";
import { PermissionGuard } from "@/components/permission-guard";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { formatDateDmyComma } from "@/utils/helper";

type TTeamId = number | null;
interface Props {
  isOpen: boolean;
  onClose: () => void;
  teamId: TTeamId;
}

export const TeamDetailsDrawer = ({ isOpen, onClose, teamId }: Props) => {
  return (
    <Drawer
      showBtn
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      styles={{ body: "h-full" }}
    >
      <TeamInformation teamId={teamId} />
    </Drawer>
  );
};

const META_COLORS: Record<string, string> = {
  orange: "bg-orange-100 text-orange-500",
  green: "bg-emerald-100 text-emerald-500",
  blue: "bg-blue-100 text-blue-500",
};

const TeamInformation = ({ teamId }: { teamId: TTeamId }) => {
  const { t } = useTranslation(["company", "teams"]);
  const { data: { data: team } = {}, isFetching } =
    useCompanyGetTeamMembersQuery(
      { teamId: teamId! },
      { skip: !teamId, refetchOnMountOrArgChange: true },
    );

  const metaInfo: {
    title: string;
    value: string;
    icon: LucideIcon;
    color: string;
  }[] = [
    {
      title: t("teams:drawer.created_label"),
      value: team?.createdAt
        ? formatDateDmyComma(team?.createdAt)
        : t("teams:drawer.created_fallback"),
      icon: CalendarPlus,
      color: "green",
    },
  ];

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-5 overflow-y-auto scrollbar-hidden">
      {/* ── Team identity ──────────────────────────────────────── */}
      <div className="flex items-center gap-4">
        <Avatar className="h-14 w-14 shrink-0">
          <AvatarImage src={team?.image ?? undefined} />
          <AvatarFallback>
            <Component size={22} />
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <h2 className="text-base font-semibold truncate">
            {team?.name ?? t("teams:drawer.untitled_team")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {team?.description ?? t("teams:drawer.no_description")}
          </p>
        </div>
      </div>

      {/* ── Meta info ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        {metaInfo.map(({ title, value, color, icon: Icon }, i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-xs border border-gray-200 bg-white px-3 py-2.5"
          >
            <div
              className={`h-9 w-9 rounded-xs shrink-0 flex items-center justify-center ${META_COLORS[color]}`}
            >
              <Icon size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {title}
              </p>
              <p className="text-xs font-medium text-gray-800 truncate">
                {value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Members section ────────────────────────────────────── */}
      <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 flex-1 min-h-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-secondary">
              {t("teams:drawer.members_section")}
            </p>
            <span className="text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
              {team?.members?.length ?? 0}
            </span>
          </div>
          {teamId && (
            <PermissionGuard
              section={PLATFORM_SECTION_ENUM.TEAMS}
              action="edit"
              mode="hide"
            >
              <Link
                to={`${PATHS.COMPANY.COMPANY_TEAMS_PATHS.EDIT(teamId)}?${hTabKey}=${TEAM_STEPS.TeamFormation}`}
                className="text-xs text-primary font-medium hover:underline"
              >
                {t("teams:drawer.edit_link")}
              </Link>
            </PermissionGuard>
          )}
        </div>

        <div className="flex flex-col gap-1.5 overflow-y-auto scrollbar-hidden flex-1 min-h-0">
          {team && (team.members?.length ?? 0) > 0 ? (
            team.members?.map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-3 rounded-xs border border-gray-200 bg-white px-3 py-2.5"
              >
                <div className="relative shrink-0">
                  <Avatar className="h-9 w-9">
                    <AvatarImage
                      src={member?.user?.profileImage ?? undefined}
                    />
                    <AvatarFallback>
                      <User size={14} />
                    </AvatarFallback>
                  </Avatar>
                  {member.role?.isTeamManager && (
                    <div
                      className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm"
                      title={t("company:shared.team_manager")}
                    >
                      <Crown size={11} className="text-yellow-500" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {startCase(member?.user?.fullName?.toLowerCase())}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {member?.role?.roleName ??
                      t("teams:drawer.no_role_assigned")}
                  </p>
                </div>
                {member.role?.isTeamManager && (
                  <span className="text-[10px] font-medium bg-yellow-50 text-yellow-600 border border-yellow-200 px-2 py-0.5 rounded-full shrink-0">
                    {t("teams:drawer.manager_badge")}
                  </span>
                )}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
              <Users size={24} className="opacity-40" />
              <p className="text-xs">{t("teams:drawer.empty_members")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
